/**
 * Play: the day's fight, the matches still going, and starting another.
 *
 * A screen of its own rather than a sidebar on the home page. It is behind
 * sign-in — the router will not put it up without a session — so nothing here
 * has to explain what an account is or what a DID is. It is the one place the
 * challenge flow starts from and the one place it comes back to.
 *
 * Only the live matches. Every match a player has ever fought is at their own
 * address, /matches/<handle-or-did>, so this screen stays what a player opens
 * to get into a game rather than growing a ledger underneath it.
 *
 * A match already going outranks the day's fight, so the in-progress section
 * is returned above the daily card — and a player with nothing going sees the
 * daily card at the top instead, because that section takes itself away rather
 * than standing there empty. One order, two screens, no condition to write.
 *
 * No page heading and no section heading over the Modes. Every block here says
 * what it is on its own face — the daily card carries its own eyebrow, the
 * matches section its own h2, and a Mode card is a title and a line — so the
 * headings were naming what was already named.
 */

import { isFinished, listMatches, type Session } from "../api";
import { footer } from "../chrome";
import { el, render } from "../dom";
import { nav } from "../nav";
import { notReadyNote, soonStamp } from "../soon-stamp";
import { dailyCard } from "./daily";
import { lobbyScreen, type ChallengeMode } from "./lobby";
import { matchList } from "./match-list";

/** One clickable entry into the challenge flow. */
function modeCard(
  title: string,
  hint: string,
  onSelect: () => void,
): HTMLElement {
  const card = el("button", { type: "button", className: "mode-card" }, [
    el("h3", { textContent: title }),
    el("p", { className: "hint", textContent: hint }),
  ]);
  card.addEventListener("click", onSelect);
  return card;
}

/** A Mode with nothing behind it yet: stamped, and not a button — there is
 * nothing to click.
 *
 * The stamp lies over the card, the same mark and the same reasoning as the
 * front page's unbuilt facts (soon-stamp.ts). It replaces the dimming this
 * card used to carry: a Mode is named here early on purpose, and a word saying
 * why it cannot be pressed beats a card faded until a reader guesses. The
 * dashed border stays, because that is the part that says "not a button".
 *
 * The heading carries the note, not the stamp — see notReadyNote. */
function futureModeCard(title: string, hint: string): HTMLElement {
  return el("div", { className: "mode-card mode-card-soon" }, [
    el("h3", {}, [title, notReadyNote()]),
    el("p", { className: "hint", textContent: hint }),
    soonStamp("mode"),
  ]);
}

export function matchesScreen(session: Session): Node[] {
  // Duel and Scenario open the same lobby screen with a different Mode:
  // Duel narrows the scenario picker to head-to-head scenarios, Scenario
  // shows the whole catalog. Nothing else about the screen differs.
  //
  // This used to open challengeScreen, the single-page all-at-once form -
  // still in the codebase, still reachable by direct import, just no longer
  // wired to either card. The lobby path is not a pure refactor of it: there
  // is no camo picker and no bot-camo-shuffle here. A human seat gets their
  // stored default camo automatically, resolved server-side at Deploy time;
  // a lobby-assigned bot seat gets no rolled camo at all, a real scope cut
  // made server-side (see matches::lobby's module doc), not an oversight
  // this screen is hiding.
  const startMatch = (mode: ChallengeMode) => () => {
    // There is no "#lobby/<id>" to navigate to yet — lobby.ts's own
    // fresh-open path mints the id and fixes the address once openLobby()
    // resolves — so this renders directly rather than through the router,
    // the same as challengeScreen did before it. That leaves the router's
    // own idea of what is on screen pointed at "matches" unless something
    // tells it otherwise; nav.markShown is that something (see nav.ts's own
    // doc), and without it a later click on the Matches link would compare
    // the address it names against a `shown` that never left "matches" and
    // do nothing.
    nav.markShown!("lobby");
    render(...lobbyScreen(session, { kind: "fresh", mode }));
  };

  // Built once and spread, rather than called in the array: dailyCard draws a
  // board of sprites, and asking it twice to test it for null would draw two.
  const daily = dailyCard();

  // A match already going is what the player came back for, so it is the top
  // of the screen. It is also the one section here that can turn out to be
  // about nothing, and an empty box is not worth that place: it takes itself
  // away once the list comes back with no live matches, which is what puts the
  // daily card at the top for a player with nothing going. That is why it is
  // built here and the list appended after — the callback that removes it has
  // to be able to name it.
  //
  // Rendered rather than held back until the list answers, so the common case
  // moves nothing: the rows replace the loading line in a section that is
  // already in place. Only a player with nothing going sees anything shift.
  const inProgress = el("section", { className: "card matches" }, [
    el("h2", { textContent: "Matches in progress" }),
  ]);
  inProgress.append(
    matchList(
      // Your own list, read as yourself: this screen is behind sign-in.
      { subject: session, session },
      listMatches,
      (match) => !isFinished(match),
      () => {
        inProgress.remove();
      },
    ),
  );

  return [
    inProgress,
    // The same card the front page carries, from the same function, so the
    // day's fight is one rendering shown in two places rather than two that
    // can disagree. Nothing scheduled drops it, here as there.
    ...(daily ? [daily] : []),
    // Two rows rather than one wrapping row, so what a Mode is worth is read
    // off its place: the three that start a match now, then the two that do
    // not. Each row divides evenly between its own cards - thirds, then halves.
    el("div", { className: "mode-rows" }, [
      el("div", { className: "mode-row" }, [
        modeCard("Duel", "Issue a 1v1 challenge", startMatch("duel")),
        modeCard(
          "Scenario",
          "Play a pre-built scenario",
          startMatch("scenario"),
        ),
        // The same Mode as Scenario today, and deliberately: there is no
        // custom-match flow yet, so Free Play opens the lobby over the whole
        // catalog rather than pointing at something that does not exist. When
        // there is one to open, this is the card that opens it.
        modeCard("Free Play", "Build a custom match", startMatch("scenario")),
      ]),
      el("div", { className: "mode-row" }, [
        futureModeCard(
          "Campaigns",
          "Play multiple matches with persistent units",
        ),
        futureModeCard("Tournaments", "Compete for glory in a tournament"),
      ]),
    ]),
    footer(),
  ];
}
